import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, X, SlidersHorizontal } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { fetchProducts } from "../lib/api";
import { useSeo } from "../lib/useSeo";

const FAMILIES = ["oriental", "boisé", "ambré", "gourmand", "floral", "épicé", "cuir", "aromatique", "frais"];
const SORT_OPTIONS = [
  { v: "", label: "Sélection" },
  { v: "price_asc", label: "Prix ↑" },
  { v: "price_desc", label: "Prix ↓" },
  { v: "newest", label: "Nouveautés" },
];

const SearchPage = () => {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const cat = params.get("category") || "";
  const collection = params.get("collection") || "";
  const family = params.get("family") || "";
  const note = params.get("note") || "";
  const min_price = params.get("min_price") || "";
  const max_price = params.get("max_price") || "";
  const sort = params.get("sort") || "";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  useSeo({
    title: q ? `Recherche : ${q} — MAISON DOUBA` : "Recherche & filtres — MAISON DOUBA",
    description: "Retrouvez les parfums MAISON DOUBA — Collection Signature (européens) et Collection Orient (Dubaï & Moyen-Orient) — par notes, famille, prix et catégorie.",
    canonical: "https://wwwmaisondouba.com/recherche",
  });

  useEffect(() => {
    setLoading(true);
    const args = {};
    if (q) args.q = q;
    if (cat) args.product_type = cat;
    if (collection) args.collection = collection;
    if (family) args.family = family;
    if (note) args.note = note;
    if (min_price) args.min_price = min_price;
    if (max_price) args.max_price = max_price;
    if (sort) args.sort = sort;
    fetchProducts(args).then(setProducts).catch(() => setProducts([])).finally(() => setLoading(false));
  }, [q, cat, collection, family, note, min_price, max_price, sort]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const clearAll = () => setParams({});

  const activeCount = useMemo(() => Array.from(params.keys()).length, [params]);

  return (
    <div className="bg-[#050505] text-white min-h-screen">
      <Navbar />
      <div className="pt-32 pb-24 max-w-7xl mx-auto px-6 md:px-12">
        <p className="label-caps text-[color:var(--gold)] mb-4">Recherche</p>
        <h1 className="font-serif text-4xl md:text-5xl mb-10">
          Trouvez votre <em className="italic text-[color:var(--gold-soft)]">fragrance</em>.
        </h1>

        {/* Search bar */}
        <div className="relative mb-6">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            data-testid="search-input"
            type="text"
            value={q}
            onChange={(e) => setParam("q", e.target.value)}
            placeholder="Chercher un parfum, une marque, une note..."
            className="input-line w-full pl-12"
          />
        </div>

        {/* Filter toggle mobile + count */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setFilterOpen(!filterOpen)}
            className="md:hidden flex items-center gap-2 label-caps text-white/70 text-xs"
          >
            <SlidersHorizontal size={14} />
            Filtres {activeCount > 0 && <span className="text-[color:var(--gold)]">({activeCount})</span>}
          </button>
          {activeCount > 0 && (
            <button onClick={clearAll} className="label-caps text-white/40 hover:text-[color:var(--gold)] text-xs flex items-center gap-1">
              <X size={12} /> Effacer les filtres
            </button>
          )}
          <select value={sort} onChange={(e) => setParam("sort", e.target.value)} className="input-line text-xs py-2 pr-8 ml-auto" data-testid="sort-select">
            {SORT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>

        <div className="grid md:grid-cols-[240px_1fr] gap-10">
          {/* Sidebar filters */}
          <aside className={`space-y-8 ${filterOpen ? "" : "hidden md:block"}`}>
            <FilterGroup title="Catégorie">
              {[["", "Tous"], ["perfume", "Parfums"], ["watch", "Montres"]].map(([v, l]) => (
                <FilterPill key={v} active={cat === v} onClick={() => setParam("category", v)}>{l}</FilterPill>
              ))}
            </FilterGroup>

            <FilterGroup title="Collection parfums">
              {[["", "Toutes"], ["Signature", "Signature (Europe)"], ["Orient", "Orient (Dubaï)"]].map(([v, l]) => (
                <FilterPill key={v} active={collection === v} onClick={() => setParam("collection", v)} testid={`filter-collection-${v || "all"}`}>{l}</FilterPill>
              ))}
            </FilterGroup>

            <FilterGroup title="Famille olfactive">
              <FilterPill active={!family} onClick={() => setParam("family", "")}>Toutes</FilterPill>
              {FAMILIES.map((f) => (
                <FilterPill key={f} active={family === f} onClick={() => setParam("family", family === f ? "" : f)}>
                  {f}
                </FilterPill>
              ))}
            </FilterGroup>

            <FilterGroup title="Note olfactive">
              <input
                data-testid="note-input"
                type="text"
                value={note}
                onChange={(e) => setParam("note", e.target.value)}
                placeholder="ex. oud, vanille, bergamote"
                className="input-line w-full text-sm"
              />
            </FilterGroup>

            <FilterGroup title="Prix (€)">
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="min-price" type="number" min="0" placeholder="Min" value={min_price} onChange={(e) => setParam("min_price", e.target.value)} className="input-line text-sm" />
                <input data-testid="max-price" type="number" min="0" placeholder="Max" value={max_price} onChange={(e) => setParam("max_price", e.target.value)} className="input-line text-sm" />
              </div>
            </FilterGroup>
          </aside>

          {/* Results grid */}
          <div>
            {loading ? (
              <p className="label-caps text-white/40 text-xs">Chargement…</p>
            ) : products.length === 0 ? (
              <div className="border border-dashed border-white/10 py-20 text-center">
                <p className="label-caps text-white/40 text-[11px] mb-4">Aucun résultat</p>
                <p className="text-white/50 text-sm">Essayez avec moins de filtres ou un autre mot-clé.</p>
              </div>
            ) : (
              <>
                <p className="label-caps text-white/40 text-[10px] mb-6">{products.length} produit{products.length > 1 ? "s" : ""}</p>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {products.map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const FilterGroup = ({ title, children }) => (
  <div>
    <p className="label-caps text-[color:var(--gold)] text-[10px] mb-4">{title}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

const FilterPill = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-1.5 text-[11px] label-caps border transition-all ${
      active ? "border-[color:var(--gold)] text-[color:var(--gold)] bg-[color:var(--gold)]/5"
             : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
    }`}
  >
    {children}
  </button>
);

export default SearchPage;
